import type { DealQuotingStatus, DealStage } from '@elka/types';

type NextActionTone = 'hot' | 'neutral' | 'info' | 'success';

export interface NextAction {
  /** Short Hebrew label displayed on the chip. */
  label: string;
  /** Optional second-line urgency hint. "· תקוע 5 ימים", "· מחכה לספק", etc. */
  urgency?: string;
  /** Tone controls the chip color; "hot" = amber attention. */
  tone: NextActionTone;
  /** Where to navigate when the chip is clicked. */
  target: 'overview' | 'pricing-matrix' | 'quote';
}

/**
 * Days elapsed since the given timestamp, or 0 if the timestamp is missing.
 */
function daysSince(ts: unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const t = ts as { toMillis?: () => number };
  if (typeof t.toMillis !== 'function') return 0;
  const elapsed = Date.now() - t.toMillis();
  return Math.max(0, Math.floor(elapsed / (24 * 60 * 60 * 1000)));
}

/**
 * Derive the next action for a deal from its quoting status + stage. This is the
 * "what to do next" chip that appears on every row in the redesigned deals list.
 *
 * Tone "hot" = amber, used when a deal has been stuck in a given status for
 * longer than the threshold we deem acceptable.
 */
export function deriveNextAction(input: {
  stage: DealStage;
  quotingStatus: DealQuotingStatus;
  updatedAt?: unknown;
}): NextAction {
  const { stage, quotingStatus, updatedAt } = input;
  const stuckDays = daysSince(updatedAt);

  if (stage === 'lost') {
    return { label: 'הפסד', tone: 'neutral', target: 'overview' };
  }
  if (stage === 'won') {
    return { label: 'זכייה', tone: 'success', target: 'overview' };
  }

  switch (quotingStatus) {
    case 'draft':
      return {
        label: 'העלה כתב כמויות',
        urgency: stuckDays > 3 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 7 ? 'hot' : 'neutral',
        target: 'overview',
      };
    case 'boq-intake':
      return {
        label: 'השלם קליטת כ״כ',
        urgency: stuckDays > 3 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 7 ? 'hot' : 'neutral',
        target: 'overview',
      };
    case 'estimating':
      return {
        label: 'פנה לספקים',
        urgency: stuckDays > 2 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 5 ? 'hot' : 'neutral',
        target: 'pricing-matrix',
      };
    case 'supplier-pricing':
      return {
        label: 'בחר מחירים',
        urgency: stuckDays > 0 ? `· ${stuckDays} ימי המתנה` : undefined,
        tone: stuckDays > 4 ? 'hot' : 'info',
        target: 'pricing-matrix',
      };
    case 'ready-to-quote':
      return {
        label: 'בנה הצעת מחיר',
        urgency: stuckDays > 1 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 3 ? 'hot' : 'neutral',
        target: 'quote',
      };
    case 'quoting':
      return {
        label: 'סיים הצעה',
        urgency: stuckDays > 1 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 3 ? 'hot' : 'info',
        target: 'quote',
      };
    case 'quoted':
      if (stage === 'negotiation') {
        return {
          label: 'עקוב אחרי לקוח',
          urgency: stuckDays > 0 ? `· ${stuckDays} ימים מאז שליחה` : undefined,
          tone: stuckDays > 7 ? 'hot' : 'info',
          target: 'overview',
        };
      }
      return {
        label: 'שלח ללקוח',
        urgency: stuckDays > 0 ? `· תקוע ${stuckDays} ימים` : undefined,
        tone: stuckDays > 3 ? 'hot' : 'neutral',
        target: 'quote',
      };
    default:
      return { label: 'פתח עסקה', tone: 'neutral', target: 'overview' };
  }
}

/**
 * Map next-action target to a route relative to a deal.
 */
export function nextActionPath(dealId: string, target: NextAction['target']): string {
  switch (target) {
    case 'pricing-matrix':
      return `/deals/${dealId}/pricing-matrix`;
    case 'quote':
      return `/deals/${dealId}/pricing`;
    case 'overview':
    default:
      return `/deals/${dealId}`;
  }
}

/**
 * 4-step progress mapping for the quoting-status column. Indices 0..3 map to
 * the four stepper dots used in the compact deals-list progress indicator.
 */
export function quotingStepIndex(status: DealQuotingStatus): number {
  switch (status) {
    case 'draft':
    case 'boq-intake':
      return 0;
    case 'estimating':
    case 'supplier-pricing':
      return 1;
    case 'ready-to-quote':
    case 'quoting':
      return 2;
    case 'quoted':
      return 3;
    default:
      return 0;
  }
}
